var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getSubject, getSender, getCreatedDate } from '../utils/emailHelpers';
import DebugService from './DebugService';
export class EmailConverterService {
    constructor() {
        // Maximum attachment size in bytes (25MB)
        this.MAX_ATTACHMENT_SIZE = 25 * 1024 * 1024;
        // Maximum base64 content length to prevent stack overflow (increased for larger files)
        // 25MB file ≈ 33.5MB in base64 (25 * 1.34), so we set limit to 35MB in base64 chars
        this.MAX_BASE64_LENGTH = 35 * 1024 * 1024; // 35MB in base64 characters
    }
    /**
     * Convert Office.js email to .eml format with all attachments
     */
    convertEmailToEml(item) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                DebugService.email('Starting EML conversion');
                // Get email metadata
                const metadata = yield this.getEmailMetadata(item);
                DebugService.object('Email metadata', metadata);
                // Get email body
                const body = yield this.getEmailBody(item);
                DebugService.debug('Email body length:', body.length);
                DebugService.debug('Email body preview:', body.substring(0, 200) + '...');
                // Get attachments with size limits
                const attachments = yield this.getEmailAttachments(item);
                DebugService.debug('Number of attachments:', attachments.length);
                // Generate .eml content
                const emlContent = this.buildEmlContent(metadata, body, attachments);
                // Validate EML content
                DebugService.debug('EML content length:', emlContent.length);
                DebugService.debug('EML content preview:', emlContent.substring(0, 500) + '...');
                // Check if EML content is valid
                if (!emlContent || emlContent.length === 0) {
                    throw new Error('Generated EML content is empty');
                }
                if (!emlContent.includes('From:') || !emlContent.includes('Subject:')) {
                    throw new Error('Generated EML content is missing required headers');
                }
                DebugService.email('EML conversion completed successfully');
                return {
                    content: emlContent
                };
            }
            catch (error) {
                DebugService.errorWithStack('Failed to convert email to EML', error);
                throw new Error(`Failed to convert email to EML: ${error}`);
            }
        });
    }
    /**
     * Get email metadata from Office.js item
     */
    getEmailMetadata(item) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d;
            // Use util helpers for subject, sender, and created date
            const subject = yield getSubject(item);
            const sender = yield getSender(item);
            const receivedTime = yield getCreatedDate(item);
            // Compose mode: recipient and attachments may differ
            let recipient = 'Unknown Recipient';
            let messageId = item.itemId || `msg_${Date.now()}`;
            let hasAttachments = false;
            let attachmentCount = 0;
            let attachmentNames = [];
            if (item.to) {
                // Read mode
                recipient = ((_b = (_a = item.to) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.emailAddress) || 'Unknown Recipient';
                if (item.attachments) {
                    hasAttachments = item.attachments.length > 0;
                    attachmentCount = item.attachments.length;
                    attachmentNames = item.attachments.map((att) => att.name);
                }
            }
            else if (item.toRecipients) {
                // Compose mode
                recipient = ((_d = (_c = item.toRecipients) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.emailAddress) || 'Unknown Recipient';
                if (item.attachments) {
                    hasAttachments = item.attachments.length > 0;
                    attachmentCount = item.attachments.length;
                    attachmentNames = item.attachments.map((att) => att.name);
                }
            }
            return {
                messageId,
                subject,
                sender,
                senderEmail: sender,
                receivedDate: receivedTime,
                receivedTime,
                hasAttachments,
                attachmentCount,
                attachmentNames,
                recipient,
                workbenchId: `UWWB_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            };
        });
    }
    /**
     * Get email body as text
     */
    getEmailBody(item) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                item.body.getAsync(Office.CoercionType.Text, (result) => {
                    var _a;
                    if (result.status === Office.AsyncResultStatus.Failed) {
                        reject(new Error(`Failed to get email body: ${((_a = result.error) === null || _a === void 0 ? void 0 : _a.message) || 'Unknown error'}`));
                        return;
                    }
                    resolve(result.value || '');
                });
            });
        });
    }
    /**
     * Get email attachments (fetches actual content as base64)
     */
    getEmailAttachments(item) {
        return __awaiter(this, void 0, void 0, function* () {
            // Compose and read both have attachments array, but content fetching may differ
            return new Promise((resolve, reject) => {
                const attachmentsArr = item.attachments || [];
                if (!attachmentsArr.length) {
                    resolve([]);
                    return;
                }
                const attachments = [];
                let processed = 0;
                const total = attachmentsArr.length;
                const finish = () => { if (processed === total)
                    resolve(attachments); };
                attachmentsArr.forEach((attachment, idx) => {
                    const attachmentSize = attachment.size || 0;
                    const attachmentName = attachment.name || 'Unknown';
                    // Check if attachment is too large
                    if (attachmentSize > this.MAX_ATTACHMENT_SIZE) {
                        DebugService.warn(`Attachment '${attachmentName}' (${this.formatFileSize(attachmentSize)}) exceeds maximum size limit. Skipping content.`);
                        attachments.push({
                            name: attachmentName,
                            contentType: attachment.contentType || 'application/octet-stream',
                            content: '',
                            size: attachmentSize,
                            isTruncated: true,
                            originalSize: attachmentSize
                        });
                        processed++;
                        finish();
                        return;
                    }
                    // Only file attachments (not item or reference)
                    if (attachment.attachmentType !== 'file') {
                        attachments.push({
                            name: attachmentName,
                            contentType: attachment.contentType || 'application/octet-stream',
                            content: '',
                            size: attachmentSize
                        });
                        processed++;
                        finish();
                        return;
                    }
                    // Compose mode: getAttachmentContentAsync may not be available, fallback to base64 if present
                    if (typeof item.getAttachmentContentAsync === 'function') {
                        item.getAttachmentContentAsync(attachment.id, { asyncContext: { idx } }, (result) => {
                            if (result.status === Office.AsyncResultStatus.Succeeded && result.value) {
                                let base64Content = '';
                                if (result.value.format === Office.MailboxEnums.AttachmentContentFormat.Base64) {
                                    base64Content = this.truncateBase64Content(result.value.content, attachmentName);
                                }
                                else if (result.value.format === Office.MailboxEnums.AttachmentContentFormat.Url) {
                                    base64Content = '';
                                }
                                attachments.push({
                                    name: attachmentName,
                                    contentType: attachment.contentType || 'application/octet-stream',
                                    content: base64Content,
                                    size: attachmentSize,
                                    isTruncated: base64Content.length < result.value.content.length,
                                    originalSize: result.value.content.length
                                });
                            }
                            else {
                                attachments.push({
                                    name: attachmentName,
                                    contentType: attachment.contentType || 'application/octet-stream',
                                    content: '',
                                    size: attachmentSize
                                });
                            }
                            processed++;
                            finish();
                        });
                    }
                    else if (attachment.contentBytes) {
                        // Compose mode: attachment content may be available as base64
                        const truncatedContent = this.truncateBase64Content(attachment.contentBytes, attachmentName);
                        attachments.push({
                            name: attachmentName,
                            contentType: attachment.contentType || 'application/octet-stream',
                            content: truncatedContent,
                            size: attachmentSize,
                            isTruncated: truncatedContent.length < attachment.contentBytes.length,
                            originalSize: attachment.contentBytes.length
                        });
                        processed++;
                        finish();
                    }
                    else {
                        attachments.push({
                            name: attachmentName,
                            contentType: attachment.contentType || 'application/octet-stream',
                            content: '',
                            size: attachmentSize
                        });
                        processed++;
                        finish();
                    }
                });
            });
        });
    }
    /**
     * Build .eml content with headers, body, and attachments
     */
    buildEmlContent(metadata, body, attachments) {
        DebugService.email('Building EML content');
        DebugService.debug('Metadata:', { subject: metadata.subject, sender: metadata.sender, recipient: metadata.recipient });
        DebugService.debug('Body length:', body.length);
        DebugService.debug('Attachments count:', attachments.length);
        const lines = [];
        // Add email headers
        lines.push(`From: ${metadata.sender}`);
        lines.push(`To: ${metadata.recipient || 'Unknown'}`);
        lines.push(`Subject: ${metadata.subject}`);
        lines.push(`Date: ${metadata.receivedTime || metadata.receivedDate || new Date().toISOString()}`);
        lines.push(`Message-ID: <${metadata.messageId}@workbench.local>`);
        lines.push(`MIME-Version: 1.0`);
        // Add workbench ID as custom header
        if (metadata.workbenchId) {
            lines.push(`X-Workbench-ID: ${metadata.workbenchId}`);
        }
        if (attachments.length > 0) {
            // Defensive: log number of attachments
            DebugService.email(`Processing ${attachments.length} attachments`);
            // Limit number of attachments processed
            const maxAttachments = 50;
            const safeAttachments = attachments.slice(0, maxAttachments);
            if (attachments.length > maxAttachments) {
                DebugService.warn(`Too many attachments (${attachments.length}), only processing first ${maxAttachments}`);
            }
            // Multipart message with attachments
            const boundary = `boundary_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            lines.push(`Content-Type: multipart/mixed; boundary="${boundary}"`);
            lines.push('');
            lines.push(`--${boundary}`);
            lines.push('Content-Type: text/plain; charset=utf-8');
            lines.push('Content-Transfer-Encoding: 7bit');
            lines.push('');
            lines.push(body);
            // Deep defensive logging
            try {
                DebugService.debug('Attachments isArray:', Array.isArray(safeAttachments), 'length:', safeAttachments.length);
                safeAttachments.forEach((att, idx) => {
                    const ctor = att && att.constructor ? att.constructor.name : typeof att;
                    const keys = att && typeof att === 'object' ? Object.keys(att) : [];
                    const contentType = att && typeof att === 'object' ? typeof att.content : 'undefined';
                    DebugService.debug(`Attachment[${idx}]: ctor=${ctor}, keys=${keys.join(',')}, contentType=${contentType}`);
                });
            }
            catch (logErr) {
                DebugService.warn('Error during attachment structure logging:', logErr);
            }
            // Add attachment placeholders
            safeAttachments.forEach((attachment, idx) => {
                lines.push('');
                lines.push(`--${boundary}`);
                lines.push(`Content-Type: ${attachment.contentType}; name="${attachment.name}"`);
                lines.push('Content-Transfer-Encoding: base64');
                lines.push(`Content-Disposition: attachment; filename="${attachment.name}"`);
                lines.push('');
                // Log type and length
                const contentType = typeof attachment.content;
                const contentLen = contentType === 'string' ? attachment.content.length : 0;
                DebugService.debug(`Attachment #${idx + 1}: ${attachment.name}, type=${contentType}, length=${contentLen}`);
                // Defensive: skip very large attachments
                const maxContentLen = 100 * 1024 * 1024; // 100MB
                if (contentType === 'string' && contentLen > maxContentLen) {
                    DebugService.warn(`Skipping attachment '${attachment.name}' due to excessive size (${contentLen} bytes)`);
                    lines.push(`[Attachment: ${attachment.name} - ${attachment.size} bytes - Skipped due to excessive size]`);
                }
                else if (attachment.content && contentType === 'string') {
                    // Check if attachment was truncated
                    if (attachment.isTruncated) {
                        DebugService.warn(`Attachment '${attachment.name}' was truncated. Original size: ${attachment.originalSize || 0}, Current size: ${attachment.content.length}`);
                        lines.push(`[Attachment: ${attachment.name} - TRUNCATED - Original size: ${this.formatFileSize(attachment.originalSize || 0)}]`);
                        lines.push('');
                    }
                    // Split base64 content into lines of 76 characters (safe, no regex)
                    let base64Lines = this.splitBase64Lines(attachment.content, 76);
                    const maxLines = 1000000; // Increased to handle larger files (was 100000)
                    // For 25MB file: ~35MB base64 / 76 chars per line = ~460,000 lines
                    // Setting to 1M lines to be safe
                    if (base64Lines.length > maxLines) {
                        DebugService.warn(`Attachment '${attachment.name}' base64 lines truncated from ${base64Lines.length} to ${maxLines} to prevent stack overflow.`);
                        base64Lines = base64Lines.slice(0, maxLines);
                    }
                    // Process base64 lines in chunks to avoid stack overflow
                    const chunkSize = 10000; // Process 10K lines at a time
                    for (let i = 0; i < base64Lines.length; i += chunkSize) {
                        const chunk = base64Lines.slice(i, i + chunkSize);
                        chunk.forEach(line => lines.push(line));
                    }
                }
                else if (attachment.content) {
                    // Log and skip if content is not a string (to prevent stack overflow)
                    DebugService.warn(`Attachment content for '${attachment.name}' is not a string. Type:`, contentType, attachment.content);
                    lines.push(`[Attachment: ${attachment.name} - ${attachment.size} bytes - Content not included due to invalid type]`);
                }
                else {
                    // Placeholder for attachment content
                    lines.push(`[Attachment: ${attachment.name} - ${attachment.size} bytes - Content not included in this export]`);
                }
            });
            lines.push('');
            lines.push(`--${boundary}--`);
        }
        else {
            // Simple text message
            lines.push('Content-Type: text/plain; charset=utf-8');
            lines.push('Content-Transfer-Encoding: 7bit');
            lines.push('');
            lines.push(body);
        }
        const finalContent = lines.join('\r\n');
        // Final validation
        DebugService.debug('Final EML content length:', finalContent.length);
        DebugService.debug('Final EML content ends with:', finalContent.substring(Math.max(0, finalContent.length - 100)));
        // Validate final content
        if (!finalContent || finalContent.length === 0) {
            throw new Error('Generated EML content is empty after joining lines');
        }
        if (!finalContent.includes('From:') || !finalContent.includes('Subject:')) {
            throw new Error('Final EML content is missing required headers');
        }
        DebugService.email('EML content built successfully');
        return finalContent;
    }
    // Helper to split base64 into 76-char lines (safe, no regex)
    splitBase64Lines(str, lineLength = 76) {
        const lines = [];
        for (let i = 0; i < str.length; i += lineLength) {
            lines.push(str.slice(i, i + lineLength));
        }
        return lines;
    }
    // Helper to truncate base64 content to prevent stack overflow
    truncateBase64Content(content, attachmentName) {
        const maxLength = this.MAX_BASE64_LENGTH;
        if (content.length <= maxLength) {
            return content;
        }
        DebugService.warn(`Truncating base64 content for attachment '${attachmentName}' to prevent stack overflow. Original length: ${content.length}, Max length: ${maxLength}`);
        return content.substring(0, maxLength);
    }
    // Helper to format file size
    formatFileSize(bytes) {
        if (bytes === 0)
            return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
}
